import React, { useCallback, useContext, useState, useEffect } from "react";
import FilePicker from "@ui-components/FilePicker";
import { NotificationsContext } from "@ui-components/Notifications";
import ConfiguratorContext from "../ConfiguratorContext";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import Input from "@ui-components/Input";
import Checkbox from "@ui-components/Checkbox";
import Modal from "@ui-components/Modal";
import { FormattedMessage, useIntl } from "react-intl";
import Button from "ui-components/Button";
import { Link } from "react-router-dom";
import Label from "@ui-components/Label";

export const TCSSurveyModal = ({
  saveResponses,
  metadata,
  alertId,
  setShowModal,
  loading,
  setLoading,
  fromService = false,
}) => {
  // hooks
  const intl = useIntl();
  const { push } = useContext(NotificationsContext);
  const countryApiRequest = useCountryAxiosInstance();
  // context
  const context = useContext(ConfiguratorContext);

  const [additionalNotes, setAdditionalNotes] = useState("");
  const [privacy, setPrivacy] = useState(false);
  const [files, setFiles] = useState([]);
  const [responses, setResponses] = useState([]);

  const { tcsType } = metadata;

  const isDisabled = useCallback(() => {
    // IF PRIVACY POLICY IS NOT CHECKED RETURN TRUE
    if (tcsType === "partner" && !privacy) {
      return true;
    }
  }, [tcsType, privacy]);

  const onSubmit = useCallback(
    async (e) => {
      try {
        e.preventDefault();

        setLoading(true);

        let attachments = [];

        if (files.length > 0) {
          attachments = await Promise.all(
            files.map(async (el) => {
              const buffer = await el.arrayBuffer();
              const uint8Array = new Uint8Array(buffer);
              const binary = uint8Array.reduce(
                (acc, byte) => acc + String.fromCharCode(byte),
                ""
              );
              const base64String = btoa(binary);

              return {
                Body: base64String,
                ContentType: el.type,
                Name: el.name,
              };
            })
          );
        }

        // if saveResponses is a function, call it and wait for the result otherwise no need to save responses
        const res = saveResponses ? await saveResponses() : true;

        if (res) {
          switch (tcsType) {
            case "kerakoll": {
              let payload = {
                worksiteId: context.survey.worksite_id,
                surveyId: context.survey.id,
                additionalQuestions: responses,
                webDescription: additionalNotes,
                attachments: attachments,
                webPrivacy: true,
                ...(metadata.webCategoriaProdotto
                  ? { webCategoriaProdotto: metadata.webCategoriaProdotto }
                  : {}),
                ...(metadata.webMotivoContatto
                  ? { webMotivoContatto: metadata.webMotivoContatto }
                  : {}),
              };
              await countryApiRequest.post("/webtocase/kerakoll", payload);
              break;
            }
            case "partner": {
              let payload = {
                worksiteId: context.survey.worksite_id,
                serviceId: context.service.id,
                surveyId: context.survey.id,
                ...(fromService ? {fromService} : {alertId: alertId}),
                additionalQuestions: responses,
                webDescription: "",
                webPrivacy: privacy,
              };
              await countryApiRequest.post("/webtocase/partner", payload);
              break;
            }
          }
          push({
            title: intl.formatMessage({
              id: "tcs_success",
              defaultMessage: "Richiesta inviata con successo",
            }),
            type: "success",
          });
        } else {
          push({
            title: intl.formatMessage({ id: "server_error" }),
            type: "error",
          });
        }
      } catch (error) {
        if (error?.response?.status === 400) {
          push({
            title: intl.formatMessage({
              id: "tcs_user_missing",
              defaultMessage: "Errore contattare un admin",
            }),
            type: "error",
          });
        } else {
          push({
            title: intl.formatMessage({ id: "server_error" }),
            type: "error",
          });
        }
      } finally {
        setLoading(false);
        setShowModal(false);
      }
    },
    [responses, additionalNotes, privacy, files] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    const { questions } = metadata;

    setResponses(
      questions.map((question) => ({
        text: question.text,
        response: "",
      }))
    );
  }, [metadata]);
  return (
    <Modal onExit={() => setShowModal(false)} submitting={loading}>
      <form className="p-2" onSubmit={onSubmit}>
        <h2 className="text-xl">
          <FormattedMessage
            id="request_support_tcs"
            defaultMessage="Richiedi supporto TCS"
          />
        </h2>
        {fromService && <p className="mt-2">{metadata.alertText}</p>}
        <div className="mt-4">
          {responses.map((el, i) => (
            <div key={i} className="flex flex-col">
              <p>{el.text}</p>
              <Input
                value={el.response}
                onChange={(e) => {
                  setResponses((prevResponses) => {
                    const updatedResponses = [...prevResponses];
                    updatedResponses[i].response = e.target.value;
                    return updatedResponses;
                  });
                }}
                required={true}
              />
            </div>
          ))}
        </div>
        {tcsType === "kerakoll" && (
          <>
            <div className="flex flex-col mb-2">
              <p className="mb-2">
                <FormattedMessage
                  id="additional_notes"
                  defaultMessage="Note aggiuntive"
                />
              </p>
              <textarea
                className="px-2 py-2 pr-10 border shadow-sm focus:ring-am-500 focus:border-am-500 block w-full sm:text-sm border-gray-300 rounded-md disabled:opacity-50"
                rows={4}
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
              />
            </div>
            <div className="flex flex-col mb-2">
              <p className="mb-2">
                <FormattedMessage
                  id="add_attachments"
                  defaultMessage="Aggiungi allegati"
                />
              </p>
              <FilePicker
                fileChosen={files}
                setFileChosen={setFiles}
                labelCustomStyle={{ maxWidth: "80%" }}
              />
            </div>
          </>
        )}
        {tcsType === "partner" && (
          <>
            <div className="flex items-center mt-2">
              <Checkbox
                checked={privacy}
                onChange={(e) => setPrivacy(e.target.checked)}
                required
              />
              <div className="pr-16 ml-2">
                <Label>
                  <FormattedMessage
                    id="consent_gdpr_alt"
                    defaultMessage="Accetto la {link}."
                    values={{
                      link: (
                        <Link
                          to={{ pathname: "/privacy-policy" }}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-black-500 font-semibold underline"
                        >
                          <span className="lowercase">
                            <FormattedMessage
                              id="privacy policy"
                              defaultMessage="privacy policy"
                            />
                          </span>
                        </Link>
                      ),
                    }}
                  />
                </Label>
              </div>
            </div>
          </>
        )}
        <Button
          type="submit"
          className="mt-4 w-full"
          submitting={loading}
          disabled={isDisabled()}
        >
          <FormattedMessage id="submit" defaultMessage="Invia" />
        </Button>
      </form>
    </Modal>
  );
};
